import { apiClient, API_BASE_URL } from "./client";
import type { SupplyFilterOption } from "./supplyApi";
import type { Assumption, ValueKind } from "./types";

// Wire types and API client calls for the D-O67 Purchasing workspace (/purchasing),
// outside-processing request review & tracking (/purchasing/requests/:key),
// and digital GSS Work Order Router (/purchasing/requests/:key/router & .docx).

export interface PurchasingWorkspaceCounts {
  totalSentCount: number;
  newCount: number;
  inReviewCount: number;
  approvedCount: number;
  printedCount: number;
  returnedCount: number;
  changedSinceApprovalCount: number;
  needsAttentionCount: number;
  draftCount: number;
  valueKind: ValueKind;
}

export interface PurchasingWatchIssue {
  issueKey: string;
  requestKey: string;
  workOrderKey: string;
  operationSequence: number | null;
  kind: string;
  severity: "Critical" | "Attention" | "Info" | "GoodNews" | string;
  severityLabel: string;
  title: string;
  summary: string;
  valueKind: ValueKind;
}

export interface PurchasingRequestOspLine {
  operationKey: string;
  sequence: number;
  sequenceFormatted: string;
  description: string;
  vendorCode: string | null;
  vendorName: string | null;
  quantity: number;
  quantityValueKind: ValueKind;
  startDate: string | null;
  needByDate: string | null;
  needByValueKind: ValueKind;
  estimatedCompletionDate: string | null;
  gssPurchaseOrder: string | null;
  trackedPoNumber: string | null;
  trackedVendorName: string | null;
  /** Release 1.7.0 (D-O141): the GSS vendor code the purchaser picked; null = GSS's. */
  trackedVendorCode?: string | null;
  /** Release 1.7.0 (D-O141): the purchaser's own ship-to address; empty = GSS's. */
  trackedVendorAddressLines?: string[];
  shipOutDate: string | null;
  promisedReturnDate: string | null;
  note: string | null;
  updatedAtUtc: string | null;
  updatedBy: string | null;
  instructionSource: string;
  usedOpcodeTextFallback: boolean;
  valueKind: ValueKind;
}

export interface EngineeringOperationStatus {
  sequence: number;
  workcenterCode: string | null;
  isClosed: boolean;
  closedAt: string | null;
  closedBy: string | null;
  valueKind: ValueKind;
}

export interface PurchasingNotificationChannelStatus {
  channel: string;
  status: string;
  deliveredCount: number;
  failedCount: number;
  skippedCount?: number;
  pendingCount?: number;
  valueKind: ValueKind;
}
export interface PurchasingWorkspaceQueueItem {
  requestKey: string;
  workOrderKey: string;
  job: string;
  suffix: string;
  partNumber: string;
  partRevision: string;
  /** D-O111: our own router number for this request, e.g. SO-RTR-000042. */
  routerNumber?: string;
  partDescription: string | null;
  customerCode: string | null;
  customerName: string | null;
  shopCode: string | null;
  quantity: number;
  quantityValueKind: ValueKind;
  earliestNeedByDate: string | null;
  needByValueKind: ValueKind;
  workOrderDueDate: string | null;
  outsideOperationCount: number;
  recordedPoCount: number;
  requestStatus: string;
  reviewStatus: "New" | "InReview" | "Approved" | "Printed" | "Returned" | "ChangedSinceApproval" | "Draft" | string;
  reviewStatusLabel: string;
  changedSinceSend: boolean;
  changedSinceApproval: boolean;
  requiresReapproval: boolean;
  sentAtUtc: string | null;
  sentBy: string | null;
  firstOpenedAtUtc: string | null;
  firstOpenedBy: string | null;
  approvedAtUtc: string | null;
  approvedBy: string | null;
  approvalCustodyId: string | null;
  lastPrintedAtUtc: string | null;
  lastPrintedBy: string | null;
  printCount: number;
  returnedAtUtc: string | null;
  returnedBy: string | null;
  returnedReason: string | null;
  outsideOperations: PurchasingRequestOspLine[];
  changesSinceSend: string[];
  changesSinceApproval: string[];
  issues: PurchasingWatchIssue[];
  valueKind: ValueKind;
  /** D-O115: where the automatic release stands (null before the first sweep looked at it). */
  autoReleaseState?: string | null;
  /** D-O115: e.g. "Waiting for GSS instructions (engineering closed 14:05)". */
  autoReleaseStateLabel?: string | null;
  autoReleaseDetail?: string | null;
  /** D-O115: "Engineering closed by <Name> on <date time>" or "Engineering closed in GSS on <date>" (recorded). */
  engineeringClosedLine?: string | null;
  engineeringClosedBy?: string | null;
  engineeringClosedValueKind?: ValueKind;
  /** D-O115: sent (or would go) without the engineer's instructions; the router says to use GSS. */
  instructionsFlagged?: boolean;
  autoReleaseCopyNumber?: string | null;  engineeringOverallStatus?: string;
  engineeringOverallValueKind?: ValueKind;
  engineeringOperations?: EngineeringOperationStatus[];
  purchasingReleasedAtUtc?: string | null;
  purchasingReleasedValueKind?: ValueKind;
  purchasingNotifiedAtUtc?: string | null;
  purchasingNotifiedValueKind?: ValueKind;
  purchasingNotificationChannels?: PurchasingNotificationChannelStatus[];
}

export interface PurchasingWorkspaceQueue {
  generatedAtUtc: string;
  dataAsOfUtc: string | null;
  isStale: boolean | null;
  dataSource: string;
  dataFloorDate: string;
  plantDate: string;
  valueKind: ValueKind;
  canApproveAndTrack: boolean;
  canSeeDrafts: boolean;
  counts: PurchasingWorkspaceCounts;
  vendors: SupplyFilterOption[];
  shops: SupplyFilterOption[];
  needsAttention: PurchasingWatchIssue[];
  requests: PurchasingWorkspaceQueueItem[];
  viperSummaryText: string;
  viperSummaryValueKind: ValueKind;
  viperSummaryAuditId: string | null;
  assumptions: Assumption[];
}

export interface PurchasingRouterEvent {
  id: number;
  requestKey: string;
  workOrderKey: string;
  eventKind: string;
  actor: string;
  occurredAtUtc: string;
  detail: string;
  custodyId: string | null;
  valueKind: ValueKind;
}

export interface PurchasingRequestDetail {
  generatedAtUtc: string;
  dataAsOfUtc: string | null;
  isStale: boolean | null;
  dataSource: string;
  dataFloorDate: string;
  plantDate: string;
  valueKind: ValueKind;
  canApproveAndTrack: boolean;
  /** Release 1.14.0 (O-R198): the ship-out date belongs to Logistics (and Planner / Operations Manager / Admin). */
  canEditShipOutDate?: boolean;
  request: PurchasingWorkspaceQueueItem;
  anyOperationUsedOpcodeTextFallback: boolean;
  opcodeTextFallbackSequences: number[];
  opcodeTextFallbackNote: string | null;
  viperInsightText: string;
  viperInsightValueKind: ValueKind;
  viperInsightAuditId: string | null;
  events: PurchasingRouterEvent[];
  assumptions: Assumption[];
}

export interface RouterPageOneHeader {
  companyName: string;
  printedTimestampText: string;
  documentTitle: string;
  workOrderDisplay: string;
  partNumber: string;
  revision: string;
  woReleasedBox: string;
  faiRequiredBox: string;
  sourceBox: string;
  dueDateText: string;
  quantityText: string;
  uomText: string;
  salesOrderText: string;
  descriptionText: string;
  orderDateText: string;
  customerQtyText: string;
  toolingChecked: boolean | null;
  gaugesMylarChecked: boolean | null;
  poPartRevisionChecked: boolean | null;
  assyComponentChecked: boolean | null;
  customerNumberText: string;
  customerNameText: string;
  customerPoNumberText: string;
  standardRouterText: string;
  prodLineText: string;
  workOrderNotesText: string;
  additionalWorkOrderCommentsText: string;
}

export interface RouterContinuationHeader {
  documentTitle: string;
  printedTimestampText: string;
  workOrderDisplay: string;
  descriptionText: string;
  dueDateText: string;
  quantityText: string;
  uomText: string;
  shipDateText: string;
  qtyShippedText: string;
  salesOrderText: string;
}

export interface RouterOperationBlock {
  sequence: number;
  sequenceFormatted: string;
  lmo: string;
  hasBarcode: boolean;
  barcodeValue: string | null;
  workcenterOrPartCode: string;
  descriptionLine: string;
  startDateText: string;
  dueDateText: string;
  uomText: string;
  laborSetupText: string;
  laborRuntimeText: string;
  laborEstHrsText: string;
  materialSetupText: string;
  materialUnitPartText: string;
  materialTotalText: string;
  instructionLines: string[];
  instructionSource: string;
  usedOpcodeTextFallback: boolean;
  operationNotesLabel: string;
  operationNoteText: string;
  valueKind: ValueKind;
  /** Printed under the operation when its instruction text is a fallback or missing (D-O98). */
  instructionNotice?: string | null;
  /** False on the later fragments of an operation that runs over a page. */
  showInstructionNotice?: boolean;
  /** Material (M) lines print "Lot: ____ Heat: ____". */
  showLotHeat?: boolean;
  /** Carried over from the previous page: no barcode, operation line or description. */
  isContinuation?: boolean;
  /** The notes and sign-off print on the next page. */
  continuesOnNextPage?: boolean;
  /** Extra space above the operation in 1/180-inch dots (a comment line that opens page 1 prints lower). */
  topGapDots?: number;
}

export interface RouterDocumentPage {
  pageNumber: number;
  totalPages: number;
  isFirstPage: boolean;
  operations: RouterOperationBlock[];
  /** D-O111: our router number (preview) or the issued copy number, printed at the left of the footer. */
  footerLeftText: string;
  footerSourceLabel: string;
  footerRightPageText: string;
}

export interface WorkOrderRouterDocument {
  requestKey: string;
  workOrderKey: string;
  /** D-O111: our own router number for this request, e.g. SO-RTR-000042. */
  routerNumber: string;
  /** D-O111: set only on an issued copy (e.g. SO-RTR-000042-02); null for the on-screen preview. */
  copyNumber?: string | null;
  sourceAttributionLabel: string;
  reviewStatus: string;
  reviewStatusLabel: string;
  approvedBy: string | null;
  approvedAtUtc: string | null;
  approvalCustodyId: string | null;
  pageOneHeader: RouterPageOneHeader;
  continuationHeader: RouterContinuationHeader;
  operations: RouterOperationBlock[];
  pages: RouterDocumentPage[];
  anyOperationUsedOpcodeTextFallback: boolean;
  opcodeTextFallbackSequences: number[];
  valueKind: ValueKind;
  /** Work-order-level GSS instruction-text copy state (D-O98); absent on older APIs = no work-order note. */
  instructionTextStatus?: "copied" | "damaged" | "pending" | "notext" | null;
  instructionTextCheckedAt?: string | null;
}

export interface UpdatePurchasingOspTrackingInput {
  poNumber: string | null;
  vendorName: string | null;
  shipOutDate: string | null;
  promisedReturnDate: string | null;
  note: string | null;
  /** Release 1.7.0 (D-O141): a vendor picked from GSS (its name fills in when vendorName is empty). */
  vendorCode?: string | null;
  /** Release 1.7.0 (D-O141): the purchaser's own ship-to address lines; empty = GSS's. */
  vendorAddressLines?: string[] | null;
}

export interface PurchasingRouterMutationOutcome {
  requestKey: string;
  workOrderKey: string;
  reviewStatus: string;
  reviewStatusLabel: string;
  custodyId: string | null;
  occurredAtUtc: string;
  summary: string;
  /** D-O111: the request's router number. */
  routerNumber?: string | null;
  /** D-O111: the copy issued by this action (print), e.g. SO-RTR-000042-02. */
  copyNumber?: string | null;
  detail: PurchasingRequestDetail;
  valueKind: ValueKind;
}

export const PURCHASING_WORKSPACE_QK = {
  queue: ["purchasing-workspace", "queue"] as const,
  detail: (key: string) => ["purchasing-workspace", "detail", key] as const,
  router: (key: string) => ["purchasing-workspace", "router", key] as const
};

export const purchasingWorkspaceApi = {
  getQueue: async (): Promise<PurchasingWorkspaceQueue> =>
    (await apiClient.get<PurchasingWorkspaceQueue>("/purchasing/requests")).data,

  getRequestDetail: async (key: string): Promise<PurchasingRequestDetail> =>
    (await apiClient.get<PurchasingRequestDetail>(`/purchasing/requests/${encodeURIComponent(key)}`)).data,

  getRouterDocument: async (key: string): Promise<WorkOrderRouterDocument> =>
    (await apiClient.get<WorkOrderRouterDocument>(`/purchasing/requests/${encodeURIComponent(key)}/router`)).data,

  routerDocxUrl: (key: string): string =>
    `${API_BASE_URL}/purchasing/requests/${encodeURIComponent(key)}/router.docx`,

  updateTracking: async (
    key: string,
    sequence: number,
    input: UpdatePurchasingOspTrackingInput
  ): Promise<PurchasingRouterMutationOutcome> =>
    (
      await apiClient.put<PurchasingRouterMutationOutcome>(
        `/purchasing/requests/${encodeURIComponent(key)}/tracking/${sequence}`,
        input
      )
    ).data,

  approveRouter: async (key: string, note?: string): Promise<PurchasingRouterMutationOutcome> =>
    (
      await apiClient.post<PurchasingRouterMutationOutcome>(
        `/purchasing/requests/${encodeURIComponent(key)}/approve`,
        { note: note ?? null }
      )
    ).data,

  returnRouter: async (key: string, reason: string): Promise<PurchasingRouterMutationOutcome> =>
    (
      await apiClient.post<PurchasingRouterMutationOutcome>(
        `/purchasing/requests/${encodeURIComponent(key)}/return`,
        { reason }
      )
    ).data,

  recordPrinted: async (key: string, format = "print"): Promise<PurchasingRouterMutationOutcome> =>
    (
      await apiClient.post<PurchasingRouterMutationOutcome>(
        `/purchasing/requests/${encodeURIComponent(key)}/printed`,
        { format }
      )
    ).data,

  submitExperienceFeedback: async (params: {
    rating: number;
    comment: string;
    page?: string;
  }): Promise<void> => {
    const cleanComment = params.comment.trim();
    const text = cleanComment
      ? `Rating: ${params.rating}/5 stars — ${cleanComment}`
      : `Rating: ${params.rating}/5 stars — Purchasing work-order router experience`;
    await apiClient.post("/feedback", {
      page: params.page ?? "/purchasing",
      text,
      source: "purchasing-router",
      helpful: params.rating >= 4,
      reason: `${params.rating}/5`
    });
  }
};
